import { getUrl } from '../bibe'
import {
  CALLBACKS_PRODUCTION_URL,
  OIDC_REFRESH_PATH,
  REFRESH_IMPERSONATION_ERROR,
  REFRESH_NO_SESSION_ERROR,
  REFRESH_UNAVAILABLE_ERROR,
} from './constants'

/**
 * `failed` means the session was rejected and the person has to sign in again.
 * `unavailable` means the refresh could not be carried out at all — the request
 * never left the browser, or the server could not reach the authorization
 * server. The session may well still be valid, so callers retry or surface the
 * error rather than logging anybody out.
 */
export type RefreshResult =
  | 'refreshed'
  | 'impersonation'
  | 'no_session'
  | 'failed'
  | 'unavailable'

const REFRESH_LOCK_NAME = 'oidc-token-refresh'

let inFlight: Promise<RefreshResult> | null = null

async function readRefreshError(
  response: Response,
): Promise<string | undefined> {
  try {
    const data: {
      ok: boolean
      error?: string
    } | null = await response.json()

    return data?.error
  } catch {
    // Empty or non-JSON body: no error code to read, treat as a generic failure.
  }

  return undefined
}

async function requestRefresh(): Promise<RefreshResult> {
  try {
    const response = await fetch(
      getUrl(`${CALLBACKS_PRODUCTION_URL}${OIDC_REFRESH_PATH}`),
      {
        method: 'POST',
        credentials: 'include',
      },
    )

    if (response.ok) {
      return 'refreshed'
    }

    const errorCode = await readRefreshError(response)

    if (errorCode === REFRESH_IMPERSONATION_ERROR) {
      return 'impersonation'
    }

    if (errorCode === REFRESH_NO_SESSION_ERROR) {
      return 'no_session'
    }

    // The status covers a 5xx from anything between here and the endpoint,
    // which answers with no body of ours to read.
    if (errorCode === REFRESH_UNAVAILABLE_ERROR || response.status >= 500) {
      return 'unavailable'
    }

    return 'failed'
  } catch {
    // fetch itself rejected: offline, DNS, CORS, a dropped connection. Nothing
    // here is a verdict on the session.
    return 'unavailable'
  }
}

/**
 * Refresh tokens are single-use: two tabs refreshing the same one concurrently
 * trips the auth server's reuse detection and logs the person out. The lock lets
 * one tab refresh at a time, and the rotated cookie reaches the others through
 * the cookie jar.
 */
function requestRefreshAcrossTabs(): Promise<RefreshResult> {
  if (typeof navigator.locks?.request !== 'function') {
    return requestRefresh()
  }

  return navigator.locks
    .request(REFRESH_LOCK_NAME, () => requestRefresh())
    .catch((): RefreshResult => 'unavailable')
}

function endsThePage(result: RefreshResult): boolean {
  return (
    result === 'impersonation' || result === 'no_session' || result === 'failed'
  )
}

/**
 * Silently refreshes the OIDC access token, which rotates the
 * `buffer_access_token` cookie server-side.
 *
 * Single-flight, and shared by every Buffer client that authenticates by the
 * cookie: an upload and a GraphQL operation rejected at the same moment renew
 * once between them.
 *
 * `impersonation`, `no_session` and `failed` end the page, because the caller
 * navigates away. Every later call in the same page load answers with that
 * first verdict and sends no second request.
 */
export function refreshAccessToken(): Promise<RefreshResult> {
  if (!inFlight) {
    inFlight = requestRefreshAcrossTabs()
      .then((result) => {
        if (!endsThePage(result)) {
          inFlight = null
        }

        return result
      })
      .catch((error: unknown) => {
        inFlight = null
        throw error
      })
  }

  return inFlight
}
